/**
 * useUnclassifiedPhotos · 「待归档」详情页照片列表（游标分页）
 *
 * 封装接口 13 /web/folder/ai/unclassified/photos 的拉取：
 *  · 游标分页（lastPageCond / search_after）· 不是页码
 *  · 返回结构对齐 FolderPhoto，复用 adaptFolderPhoto 映射成 PhotoItem
 *  · loading / hasMore / total 状态供页面渲染 + infinite scroll
 *
 * 副作用归调用方：本 hook 只负责拉数据 + 维护分页状态，不弹 toast / 不跳转。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import { fetchUnclassifiedPhotos } from '../../../slices/aiClassify'
import type { PhotoItem } from '../Detail/PhotoGrid'
import { adaptFolderPhoto } from '../Detail/utils/adaptPhoto'
import type { BackendPhoto } from '../Detail/utils/adaptPhoto'

/** 每页拉取张数 · 接口默认 200（未分类可能上万，必须游标分页） */
const PAGE_SIZE = 200

/**
 * 单次 loadMore 至少凑够多少张 · 待归档稀疏 + 服务端逐页计算慢，一页可能 0 张
 * （本批 200 张都已归档）或只有几张。单次 loadMore 跨页累加，直到凑够这么多张
 * 「或翻到最后一页（游标空）」为止——这样只要还没到底，已加载的就够铺满一屏、
 * 滚动条出得来，后续由 IntersectionObserver 滚动续拉，无需「加载更多」按钮。
 * 卸载 / 切团队由 runId 令牌即时中断在途请求。
 */
const TARGET_PHOTOS_PER_LOAD = 100

/** useUnclassifiedPhotos 返回值 */
export interface UseUnclassifiedPhotosResult {
  /** 已加载到的照片（已 adapt） */
  photos: PhotoItem[]
  /** 是否正在请求某一页 */
  loading: boolean
  /** 是否还有下一页 · 游标空串即到底 */
  hasMore: boolean
  /** 是否加载出错（首页或翻页失败） */
  error: boolean
  /** 拉下一页 · infinite scroll / 预览续拉调用；并发与到底已内部守卫 */
  loadMore: () => void
}

/**
 * 拉取并分页管理待归档（未分类）照片列表。
 *
 * 由 Unclassified 详情页调用：mount / groupID 变化时重置并拉首页，
 * 滚动到底由页面把 loadMore 接到 PhotoGrid 的 onLoadMore。
 *
 * @param groupID - 团队 id；未就绪（空）时不发请求
 * @returns 照片列表、总数、加载状态与翻页方法，含义见 UseUnclassifiedPhotosResult
 */
export function useUnclassifiedPhotos(
  groupID: string | number | undefined
): UseUnclassifiedPhotosResult {
  const dispatch = useAppDispatch()
  const [photos, setPhotos] = useState<PhotoItem[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState(false)
  /** 下一页游标 · 首页为空串；用 ref 持有避免闭包拿到旧值 */
  const cursorRef = useRef('')
  /** 并发守卫 · 防止 infinite scroll 在同一页未回来时重复触发 */
  const fetchingRef = useRef(false)
  /**
   * 轮次令牌 · 卸载 / 切团队时由 effect 清理自增，作废在途的 loadMore：
   * 在途请求回来后发现令牌已变 → 丢弃结果、停止继续翻页，避免「切走页面后接口
   * 还一直在翻」，也避免把已卸载组件 / 旧团队的数据写进 state。
   */
  const runIdRef = useRef(0)

  const loadMore = useCallback(async () => {
    if (!groupID || fetchingRef.current) return
    // 记下本轮令牌；await 回来后若令牌已变（卸载 / 切团队）则作废本轮
    const myRun = runIdRef.current
    fetchingRef.current = true
    setLoading(true)
    try {
      // 待归档为 membership 游标分页（feat/0614，与「全部照片/tree」一致）：
      //  · 只靠 lastPageCond 空串判定到底，不用 totalCount（恒 0，不可用）
      //  · 单页可能 0 张或只有几张 —— 跨页累加，直到本轮凑够
      //    TARGET_PHOTOS_PER_LOAD 张，或翻到最后一页（游标空）为止
      //  · 单页也可能 > pageSize，不假设恰好等于 pageSize
      let loadedThisRound = 0
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await dispatch(
          fetchUnclassifiedPhotos({
            groupID,
            pageSize: PAGE_SIZE,
            lastPageCond: cursorRef.current
          })
        )
        // 本轮已作废（组件卸载 / 切团队）→ 立即停止：不再发后续请求、不写 state
        if (runIdRef.current !== myRun) return
        // thunk 已把业务失败 / 网络错误统一兜成 undefined
        if (!result) {
          setError(true)
          setHasMore(false)
          break
        }
        const mapped = (result.photos as BackendPhoto[]).map(adaptFolderPhoto)
        cursorRef.current = result.lastPageCond
        const more = result.lastPageCond !== ''
        if (mapped.length > 0) {
          setPhotos((prev) => [...prev, ...mapped])
          loadedThisRound += mapped.length
        }
        setHasMore(more)
        // 凑够 TARGET_PHOTOS_PER_LOAD 张 / 翻到最后一页 → 结束本轮；否则继续翻
        if (loadedThisRound >= TARGET_PHOTOS_PER_LOAD || !more) break
      }
    } finally {
      // 仅当仍是本轮才复位 · 已作废的轮次别覆盖新轮次的 fetchingRef / loading
      if (runIdRef.current === myRun) {
        fetchingRef.current = false
        setLoading(false)
      }
    }
  }, [dispatch, groupID])

  // mount / groupID 变化 → 重置分页状态并拉首页
  useEffect(() => {
    setPhotos([])
    setError(false)
    setHasMore(true)
    cursorRef.current = ''
    fetchingRef.current = false
    if (groupID) loadMore()
    // 卸载 / groupID 变化 → 自增令牌作废在途 loadMore（停止后续请求 + 丢弃结果）
    return () => {
      runIdRef.current += 1
    }
    // loadMore 依赖 groupID，groupID 变时已是新引用；仅在 groupID 变化时重置
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupID])

  return { photos, loading, hasMore, error, loadMore }
}
